import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";

interface SiiCredentialsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (rut: string, password: string) => Promise<void>;
}

export function SiiCredentialsModal({
  open,
  onOpenChange,
  onSave,
}: SiiCredentialsModalProps) {
  const [rut, setRut] = useState("");
  const [password, setPassword] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    if (!rut || !password) {
      setError("Ingrese el RUT y la clave tributaria");
      return;
    }
    setError(null);
    setIsSaving(true);
    try {
      await onSave(rut, password);
      setRut("");
      setPassword("");
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al guardar");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Credenciales Portal SII</DialogTitle>
          <DialogDescription>
            Para sincronizar el Registro de Compras y Ventas necesitamos las
            credenciales de acceso al portal web del SII.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="modal-sii-rut">RUT</Label>
            <Input
              id="modal-sii-rut"
              placeholder="12345678-9"
              value={rut}
              onChange={(e) => setRut(e.target.value)}
              autoFocus
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="modal-sii-password">Clave SII</Label>
            <Input
              id="modal-sii-password"
              type="password"
              placeholder="Ingrese la clave"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={!rut || !password || isSaving}>
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Guardando...
              </>
            ) : (
              "Guardar y Sincronizar"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
